import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers } from '../../../screens/utils';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';

import { useValidateIbanMutation } from '../../../services/redux/features/api/ibanValidator.api-slice';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { BankDetailsFormModel_MX } from './formModel';
import { getInitialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
  titleTranslationKey: string;
};

export const BankDetailsNew_MX: React.FC<Props> = ({ formHelpers, titleTranslationKey }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<Partial<BankDetailsFormModel_MX>>>();
  const [validateIban] = useValidateIbanMutation();

  useEffect(() => {
    setValidationSchema(getValidationSchema(validateIban));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { formSectionProps, onFormSubmitSuccess, onStartEditInSummaryMode, isDisabledFormSummary } = formHelpers;

  const onSubmit = (values: BankDetailsFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(titleTranslationKey), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent disabled={isDisabledFormSummary} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  disabled: boolean;
  formHelpers: FormHelpers;
};

export const FormContent: React.FC<FormContentProps> = ({ disabled, formHelpers }) => {
  const { submitForm, values } = useFormikContext<BankDetailsFormModel_MX>();
  const { onFormValuesChange } = formHelpers;

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(disableAllFormControls));
  }, [disableAllFormControls]);

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="2/3" s="1/1">{inputs.iban()}</Layout.Item>
          <Layout.Item default="1/3" s="1/1">{inputs.preferredPaymentDay()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="2/3" s="1/1">{inputs.bankAccountHolder()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers} />
    </Form>
  );
};
